import { useMemo, useState } from "react";
import { faCheck, faFilter } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Menu, MenuProps, ScrollArea, TextInput } from "@mantine/core";

export interface CompactSingleSelectProps {
  options: string[];
  selectedItem?: string;
  onChange: (item: string) => void;
  isItemLocked?: (item: string) => boolean;
  searchable?: boolean;
  menuProps?: MenuProps;
}

/**
 *
 * @param options - the options to select from. These must be unique.
 * @param selectedItem - the item that is currently selected, undefined if none selected
 * @param onChange - a callback that is called when the selection changes
 * @param isItemLocked - a callback that returns true if the item is locked and cannot be selected/deselected
 * @param searchable - whether a search box is shown to filter the options
 * @param menuProps - props to pass to the menu ex. position="bottom-end"
 * @returns
 */
export const CompactSingleSelect: React.FC<CompactSingleSelectProps> = ({
  options,
  selectedItem,
  onChange,
  isItemLocked,
  searchable,
  menuProps,
  children,
}) => {
  const [filterText, setFilterText] = useState<string>();

  const filteredOptions = useMemo(() => {
    if (!filterText) {
      return options;
    }
    return options.filter((option) =>
      option.toLowerCase().includes(filterText.toLowerCase())
    );
  }, [filterText, options]);

  return (
    <Menu {...menuProps}>
      <Menu.Target>{children}</Menu.Target>
      <Menu.Dropdown p={0}>
        {searchable && (
          <TextInput
            m="4px"
            mb="xs"
            placeholder="Filter"
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            icon={<FontAwesomeIcon icon={faFilter} />}
          />
        )}
        <ScrollArea.Autosize p={0} maxHeight={200} offsetScrollbars>
          {/* padding here makes it so the outline is not cut off by the scroll area */}
          <Box sx={{ padding: "4px" }}>
            {filteredOptions.map((option) => (
              <Menu.Item
                key={option.replace(/ /g, "-")}
                data-testid={option.replace(/ /g, "-")}
                value={option}
                onClick={() => onChange(option)}
                disabled={isItemLocked && isItemLocked(option)}
                icon={
                  option === selectedItem ? (
                    <FontAwesomeIcon icon={faCheck} />
                  ) : undefined
                }
                sx={
                  option !== selectedItem ? { paddingLeft: "34px" } : undefined
                }
              >
                {option}
              </Menu.Item>
            ))}
          </Box>
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
};
